<template lang="pug">
#leaderboard.insights-leaderboard-widget.insights-widget(
  :class="remoteVsOffice ? 'remote-vs-office' : ''"
)
  .widget-wrapper
    InsightsLeaderboardDialog(
      v-if="hasDialog && showLeaderboardDialog"
      :show="showLeaderboardDialog"
      :size="dialogSize"
      @close="toggleDialog"
    )
    .widget-panel.widget-common.widget-large.widget-full-height
      .widget-title.d-flex.justify-content-between.align-items-center.mb-20
        .d-flex.align-items-center
          span.title-label(:class="titleClasses") {{ widgetTitle }}
          template(v-if="!isEmptyWidget && !remoteVsOffice")
            AppSwitch.title-switch.leaderboard-metrics.cursor-pointer(
              v-model="showMetrics"
              label="Trend data"
              text-position="right"
            )
            i.hi.hi-info-filled.hi-12.a-tooltip.leaderboard-metrics(
              v-tooltip="tooltipOptions"
              title="Trend data is calculated using the selected time frame until the present date against the same period of the previous time frame."
            )
        slot(name="action")
          a.insights-widget-view-more(
            v-if="hasData"
            v-track:click="trackToggleProperties('view all')"
            @click.prevent="toggleDialog"
          )
            i.hi.hi-external
            span View all

      .widget-body.overflow-x-auto(:class="bodyClasses")
        template(v-if="!isFirstLoad")
          .table-wrapper(v-if="hasDataOrLoading")
            template(v-if="scrollable")
              AppInfiniteScroll.infinite-scroll.w-full(
                ref="scroller"
                max-height="82vh"
                @loadmore="loadMoreData"
              )
                InsightsLeaderboardTable(
                  v-track:click="trackToggleProperties('sort')"
                  :data="tableData"
                  :loading="!isReady"
                  :sort-options="sortOptions"
                  :show-metrics="showMetrics"
                  @sort="sort"
                )
            InsightsLeaderboardTable(
              v-else
              v-track:click="trackToggleProperties('sort')"
              :data="tableData"
              :loading="!isReady"
              :sort-options="sortOptions"
              :show-metrics="showMetrics"
              @sort="sort"
            )
          InsightsNoData(
            v-else
            :type="emptyReason"
          )
</template>

<script>
import { INSIGHTS } from '~/app_constants';
import { Insights, InsightsTracking } from '~/mixins';
import { mapActions, mapGetters, mapState } from 'vuex';

import InsightsLeaderboardTable from './InsightsLeaderboardTable.vue';

import AppInfiniteScroll from '../AppInfiniteScroll.vue';
import AppSwitch from '~/components/AppSwitch.vue';

export default {
  name: 'InsightsLeaderboardWidget',
  components: {
    InsightsLeaderboardTable,
    AppInfiniteScroll,
    AppSwitch,
    InsightsLeaderboardDialog: () => import('./InsightsLeaderboardDialog.vue'),
    InsightsNoData: () => import('~/components/insights/InsightsNoData.vue'),
  },
  mixins: [Insights, InsightsTracking],
  props: {
    titleClasses: {
      type: String,
      default: '',
    },
    scrollable: {
      type: Boolean,
      default: false,
    },
    hasDialog: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    widgetKey: INSIGHTS.api.leaderboard,
    widgetLimit: 10,
    limit: 20,
    offset: 20,
    showLeaderboardDialog: false,
    title: 'Leaderboard',
    currentSortOption: {},
  }),
  computed: {
    ...mapGetters({
      widgetData: 'leaderboard',
    }),
    ...mapGetters(['remoteVsOffice', 'leaderboardShowMetrics']),
    ...mapState(['loadingWidgets']),
    showMetrics: {
      get() {
        return this.leaderboardShowMetrics;
      },
      set(value) {
        this.setLeaderboardShowMetrics(value);
      },
    },
    isLoading() {
      return this.loadingWidgets[this.widgetKey];
    },
    hasData() {
      return !this.isEmptyWidget;
    },
    hasDataOrLoading() {
      return !this.isEmptyWidget || !this.isReady;
    },
    tableData() {
      const data = this.widgetData?.data || [];

      return this.scrollable ? data : data.slice(0, this.widgetLimit);
    },
    tooltipOptions() {
      return { placement: this.scrollable ? 'right' : 'top' };
    },
    sortOptions() {
      return this.widgetData.sort || {};
    },
    isFullyLoaded() {
      return !!this.widgetData.isFullyLoaded;
    },
    dialogSize() {
      return this.remoteVsOffice ? 'x-large' : 'large';
    },
    widgetTitle() {
      return this.remoteVsOffice ? 'Member overview' : 'Leaderboard';
    },
  },
  async created() {
    // Skip initial fetch if widget data already exists (fetched globally)
    if (!this.scrollable && !this.hasDataOrLoading) {
      await this.fetchWidgets({ widgetKey: this.widgetKey });
    }

    this.currentSortOption = { sort_by: this.sortOptions.field, sort_order: this.sortOptions.direction };

    this.isFirstLoad = false;
  },
  methods: {
    ...mapActions(['fetchWidgets', 'setFullyLoaded', 'setLeaderboardShowMetrics']),
    sort(options) {
      if (this.$refs.scroller) {
        this.$refs.scroller.$el.scrollTo(0, 0);
      }
      this.offset = this.limit;

      this.currentSortOption = {
        sort_by: options.field,
        sort_order: options.direction,
      };
      this.setFullyLoaded({ widgetName: this.widgetKey, value: false });
      this.fetchWidgets({
        widgetKey: this.widgetKey,
        extraQueryParams: { ...this.currentSortOption },
      });
    },
    toggleDialog() {
      this.showLeaderboardDialog = !this.showLeaderboardDialog;
    },
    setLoadedState(value = 0) {
      const state = !(this.widgetData?.data?.length % this.limit === 0 && this.widgetData.data.length !== value);

      this.setFullyLoaded({ widgetName: this.widgetKey, value: state });
    },
    async loadMoreData() {
      if (this.isLoading) {
        return;
      }
      if (!this.isEmptyWidget && !this.isFullyLoaded) {
        const dataLength = this.widgetData.data.length;

        await this.fetchWidgets({
          widgetKey: this.widgetKey,
          extraQueryParams: { offset: this.offset, ...this.currentSortOption },
          persists: this.scrollable,
          lazy: true,
        });
        this.offset = this.offset + this.limit;
        this.setLoadedState(dataLength);
      }
    },
    trackToggleProperties(type) {
      return Object.freeze({
        label: 'Widget click',
        widget_name: this.title,
        action_type: type,
        action_label: type === 'sort' ? 'Sort' : 'View all',
        ...this.commonProps(),
      });
    },
  },
};
</script>
